import { trackEvent, AnalyticsEvents } from '../../../analytics';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActionIcon,
  Anchor,
  Button,
  Stack,
  Loader,
  Modal,
  Progress,
  Select,
  Switch,
  Text,
} from '@mantine/core';
import {
  IconCheck,
  IconCloud,
  IconCloudUpload,
  IconAlertCircle,
  IconClock,
  IconArrowLeft,
  IconArrowRight,
  IconUsers,
  IconX,
  IconSparkles,
  IconExternalLink,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetPerformanceReviewFormsId,
  getGetPerformanceReviewFormsIdQueryKey,
  useGetPerformanceReviewQuestionnairesId,
  useGetPerformanceReviewRoundsId,
  usePatchPerformanceReviewFormsIdDraft,
  usePostPerformanceReviewFormsIdSubmit,
  useGetPerformanceReviewRoundsRoundIdParticipants,
  usePatchPerformanceReviewRoundsRoundIdParticipantsUserIdRequestedReviewers,
} from '../../../api/generated/performance-review/performance-review';
import { QuestionField } from '../../../components/review/QuestionField';
import { useUsersDirectory } from '../../../hooks/useUserApi';
import { useDevice } from '../../../hooks/useDevice';
import { useUserStore } from '../../../store/userStore';
import { PerformanceReviewForm } from '../../../api/generated/model/performanceReviewForm';
import { PerformanceReviewQuestionnaire } from '../../../api/generated/model/performanceReviewQuestionnaire';
import { PerformanceReviewQuestion } from '../../../api/generated/model/performanceReviewQuestion';
import { User } from '../../../types';
import { showToast } from '../../../utils/toast';
import { api, getErrorMessage } from '../../../utils/api';
import { UserBadge } from '../../../components/user/UserBadge';
import { LoadFailed } from '../../../components/ui/LoadFailed';
import { SubjectGoalsPanel } from '../parts/SubjectGoalsPanel';

type PillTone = 'accent' | 'warn' | 'danger' | 'neutral';
const Pill: React.FC<{ tone?: PillTone; size?: 'sm' | 'md'; children: React.ReactNode }> = ({
  tone = 'neutral',
  size = 'sm',
  children,
}) => {
  const map: Record<PillTone, { bg: string; fg: string }> = {
    accent: { bg: 'var(--se-accent-soft)', fg: 'var(--se-accent-deep)' },
    warn: { bg: 'var(--se-warn-soft)', fg: 'var(--se-warn)' },
    danger: { bg: 'var(--se-danger-soft)', fg: 'var(--se-danger)' },
    neutral: { bg: 'var(--se-surface-2)', fg: 'var(--se-ink-2)' },
  };
  const tt = map[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: size === 'sm' ? '3px 9px' : '5px 11px',
        background: tt.bg,
        color: tt.fg,
        borderRadius: 999,
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
};

const Card: React.FC<{
  children: React.ReactNode;
  padding?: number | string;
  radius?: 'md' | 'lg';
  style?: React.CSSProperties;
}> = ({ children, padding = 24, radius = 'lg', style }) => (
  <div
    style={{
      background: 'var(--se-surface)',
      border: '1px solid var(--se-line)',
      borderRadius: radius === 'lg' ? 12 : 8,
      padding,
      ...style,
    }}
  >
    {children}
  </div>
);

const MIN_GOOD_CHARS = 80;

interface RailItem {
  id: string;
  title: string;
  kind: 'check' | 'icon';
  label?: number;
  required?: boolean;
  filled: boolean;
}

const RailItemRow: React.FC<{
  item: RailItem;
  active: boolean;
  isLast: boolean;
  onClick: () => void;
}> = ({ item, active, isLast, onClick }) => {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: '20px 1fr 16px',
        gap: 10,
        alignItems: 'center',
        padding: '8px 10px',
        borderRadius: 6,
        background: active ? 'var(--se-accent-soft)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background 120ms',
        color: 'inherit',
      }}
    >
      <span style={{ position: 'relative', width: 20, display: 'flex', justifyContent: 'center' }}>
        {item.kind === 'check' ? (
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 999,
              background: item.filled ? 'var(--se-accent)' : 'var(--se-surface)',
              color: item.filled ? '#fff' : 'var(--se-soft)',
              border: item.filled ? 'none' : '1px solid var(--se-line-strong)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            {item.filled ? <IconCheck size={11} /> : item.label}
          </span>
        ) : (
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 6,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: item.filled ? 'var(--se-accent-soft)' : 'var(--se-surface-2)',
              color: item.filled ? 'var(--se-accent-deep)' : 'var(--se-muted)',
            }}
          >
            <IconUsers size={12} />
          </span>
        )}
        {!isLast && (
          <span
            style={{
              position: 'absolute',
              top: 18,
              bottom: -10,
              left: '50%',
              width: 1,
              marginLeft: -0.5,
              background: item.filled ? 'var(--se-accent)' : 'var(--se-line)',
            }}
          />
        )}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: active ? 500 : 400,
            color: active ? 'var(--se-accent-deep)' : 'var(--se-ink-2)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.title}
        </div>
        {item.required && !item.filled && (
          <div style={{ fontSize: 10, color: 'var(--se-warn)', marginTop: 1, fontWeight: 500 }}>
            обязательный
          </div>
        )}
      </div>
      {active && (
        <span
          style={{
            width: 5,
            height: 5,
            borderRadius: 999,
            background: 'var(--se-accent)',
            display: 'inline-block',
          }}
        />
      )}
    </button>
  );
};

const AutoSizeTextarea: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, onChange, placeholder, disabled }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = 'auto';
    ref.current.style.height = ref.current.scrollHeight + 'px';
  }, [value]);

  return (
    <textarea
      ref={ref}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      disabled={disabled}
      rows={4}
      style={{
        width: '100%',
        minHeight: 110,
        padding: '14px 16px',
        border: '1px solid ' + (focused ? 'var(--se-accent)' : 'var(--se-line-strong)'),
        boxShadow: focused ? '0 0 0 3px var(--se-accent-soft)' : 'none',
        borderRadius: 'var(--se-radius)',
        background: 'var(--se-bg)',
        color: 'var(--se-ink)',
        fontSize: 14,
        lineHeight: 1.6,
        resize: 'none',
        outline: 'none',
        fontFamily: 'inherit',
        transition: 'border-color 120ms, box-shadow 120ms',
      }}
    />
  );
};

export const PerformanceReviewFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const device = useDevice();
  const isNarrow = device !== 'desktop';
  const me = useUserStore((s) => s.user) as User | undefined;

  const { data: form, isError: isFormError } = useGetPerformanceReviewFormsId(id!);
  const f = form as unknown as PerformanceReviewForm | undefined;
  const { data: round, isError: isRoundError } = useGetPerformanceReviewRoundsId(f?.round || '', {
    query: { enabled: !!f?.round },
  });
  const r = round as unknown as
    | {
        _id?: string;
        title?: string;
        questionnaire?: string;
        endDate?: string;
        stageDeadlines?: { collectEssay?: string };
        stage?: string;
      }
    | undefined;
  const { data: questionnaire, isError: isQuestionnaireError } =
    useGetPerformanceReviewQuestionnairesId(r?.questionnaire || '', {
      query: { enabled: !!r?.questionnaire },
    });
  const q = questionnaire as unknown as PerformanceReviewQuestionnaire | undefined;
  const { data: participantsData } = useGetPerformanceReviewRoundsRoundIdParticipants(
    f?.round || '',
    { query: { enabled: !!f?.round } },
  );

  // Reviewer picker is for regular employees, so it must use the auth-only
  // directory endpoint — GET /users is admin-only and 403s for them.
  const { data: usersData } = useUsersDirectory();

  const saveDraft = usePatchPerformanceReviewFormsIdDraft();
  const submit = usePostPerformanceReviewFormsIdSubmit();
  const setRequested = usePatchPerformanceReviewRoundsRoundIdParticipantsUserIdRequestedReviewers();
  const queryClient = useQueryClient();
  const [isReopening, setIsReopening] = useState(false);

  // Keep the form query cache in sync with what we just persisted. Without this,
  // the GET-form cache keeps its pre-edit snapshot; navigating away and back
  // re-hydrates the editor from that stale snapshot, showing already-saved
  // answers as empty (and a subsequent autosave would then overwrite them).
  const syncFormCache = (saved: unknown) => {
    if (id && saved) queryClient.setQueryData(getGetPerformanceReviewFormsIdQueryKey(id), saved);
  };

  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [wantsGradePromotion, setWantsGradePromotion] = useState(false);
  const [requestedReviewers, setRequestedReviewers] = useState<string[]>([]);
  const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>(
    'idle',
  );
  const [activeId, setActiveId] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const lastSavedAnswersRef = useRef<string>('');
  const lastSavedPromotionRef = useRef<string>('false');
  const lastSavedReviewersRef = useRef<string>('');
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionNodes = useRef<Record<string, HTMLDivElement | null>>({});

  // hydrate answers from form
  useEffect(() => {
    if (f?.answers) {
      const map: Record<string, unknown> = {};
      for (const a of f.answers) {
        if (a.questionId != null) {
          map[a.questionId] = a.value;
        }
      }
      setAnswers(map);
      const promotion = (f as { wantsGradePromotion?: boolean }).wantsGradePromotion === true;
      setWantsGradePromotion(promotion);
      lastSavedAnswersRef.current = JSON.stringify(map);
      lastSavedPromotionRef.current = JSON.stringify(promotion);
      setAutosaveStatus('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f?._id]);

  // hydrate requestedReviewers from participant
  useEffect(() => {
    const myId = me?._id;
    if (!myId) return;
    const ps =
      (participantsData as unknown as Array<{
        _id?: string;
        user?: { _id?: string } | string;
        requestedReviewers?: string[];
      }>) || [];
    const mine = ps.find((p) => {
      const u = p.user;
      return typeof u === 'object' && u !== null ? u._id === myId : u === myId;
    });
    if (mine?.requestedReviewers) {
      setRequestedReviewers(mine.requestedReviewers);
      lastSavedReviewersRef.current = JSON.stringify(mine.requestedReviewers);
    }
  }, [participantsData, me?._id]);

  // Сбор формы своего типа закрыт: self-эссе сдают до конца сбора (просроченное — и на
  // collect-feedback, решение E6-5), отзыв — на collect-feedback. Дальше форма только для
  // чтения, автосейв не шлёт заведомые 409.
  // Тот же явный список, что COLLECT_STAGES_OF на бэкенде.
  const collectStages =
    f?.type === 'self' ? ['collect-essay', 'collect-feedback'] : ['collect-feedback'];
  const isCollectionClosed = !!r?.stage && !collectStages.includes(r.stage);

  // Debounced autosave for both answers and requested reviewers.
  useEffect(() => {
    if (!f || f.status === 'submitted' || !id || isCollectionClosed) return;
    const answersJson = JSON.stringify(answers);
    const promotionJson = JSON.stringify(wantsGradePromotion);
    const reviewersJson = JSON.stringify(requestedReviewers);
    const answersDirty = answersJson !== lastSavedAnswersRef.current;
    const promotionDirty = promotionJson !== lastSavedPromotionRef.current;
    const reviewersDirty = reviewersJson !== lastSavedReviewersRef.current;
    if (!answersDirty && !promotionDirty && !reviewersDirty) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      setAutosaveStatus('saving');
      try {
        if (answersDirty || promotionDirty) {
          const saved = await saveDraft.mutateAsync({
            id,
            data: {
              answers: Object.entries(answers).map(([questionId, value]) => ({
                questionId,
                value: value as string | number | string[] | null,
              })),
              wantsGradePromotion,
            } as never,
          });
          syncFormCache(saved);
          lastSavedAnswersRef.current = answersJson;
          lastSavedPromotionRef.current = promotionJson;
        }
        if (reviewersDirty && me?._id && f.round && r?.stage === 'collect-essay') {
          await setRequested.mutateAsync({
            roundId: f.round,
            userId: me._id,
            data: { reviewerIds: requestedReviewers },
          });
          lastSavedReviewersRef.current = reviewersJson;
        }
        setAutosaveStatus('saved');
      } catch {
        setAutosaveStatus('error');
      }
    }, 1000);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    answers,
    wantsGradePromotion,
    requestedReviewers,
    id,
    f?._id,
    f?.status,
    me?._id,
    f?.round,
    isCollectionClosed,
  ]);

  // Scroll-spy for the side rail.
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const sid = (e.target as HTMLDivElement).dataset.sectionId;
            if (sid) setActiveId(sid);
          }
        }
      },
      { rootMargin: '-30% 0px -55% 0px' },
    );
    Object.values(sectionNodes.current).forEach((n) => n && obs.observe(n));
    return () => obs.disconnect();
  }, [q?._id]);

  if (isFormError || isRoundError || isQuestionnaireError)
    return <LoadFailed onBack={() => navigate('/performance-review')} />;
  if (!f || !q)
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 80,
        }}
      >
        <Loader />
      </div>
    );

  if (f.type !== 'self') {
    return (
      <Stack align="center" gap="md" p={40}>
        <Text c="dimmed">{t('perfReview.modalForSelfNote')}</Text>
        <Button variant="light" onClick={() => navigate('/performance-review')}>
          {t('perfReview.openPerfReview')}
        </Button>
      </Stack>
    );
  }

  const questions = (q.selfForm?.questions || []) as PerformanceReviewQuestion[];
  const subject =
    f.subject && typeof f.subject === 'object' ? (f.subject as unknown as User) : undefined;
  const currentGradeLink =
    subject?.competencyMapId && subject.grade?._id
      ? `/competency-maps/${subject.competencyMapId}/${subject.grade._id}`
      : null;
  // Treat any non-draft status as immutable. Backend only ever toggles between
  // draft → submitted today, but `archived` should also lock the form if it
  // ever appears on this status enum.
  const readOnly = (f.status !== 'draft' && f.status !== 'pending') || isCollectionClosed;
  const isReviewersLocked = readOnly || r?.stage !== 'collect-essay';
  // Бэкенд разрешает автору вернуть отправленное эссе в черновик, пока идёт этап
  // эссе; раньше кнопка для этого была только в модалке отзывов о коллегах.
  const canReopen =
    f.status === 'submitted' &&
    (f.type === 'self' ? !isCollectionClosed : r?.stage === 'collect-essay');
  const handleReopen = async () => {
    setIsReopening(true);
    try {
      const reopened = await api(`/performance-review/forms/${f._id}/reopen`, { method: 'POST' });
      syncFormCache(reopened);
      showToast({
        title: t('perfReview.reopenedTitle'),
        message: t('perfReview.reopenedMessage'),
        color: 'blue',
      });
    } catch (e) {
      showToast({
        title: t('perfReview.reopenFailed'),
        message: getErrorMessage(e, ''),
        color: 'red',
      });
    } finally {
      setIsReopening(false);
    }
  };

  const stringValue = (id: string) => {
    const v = answers[id];
    return typeof v === 'string' ? v : '';
  };

  const isAnswered = (qq: PerformanceReviewQuestion): boolean => {
    const v = answers[qq.id ?? ''];
    if (v == null) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'number') return true;
    return false;
  };

  // Progress
  const answeredCount = questions.filter(isAnswered).length;
  const reviewerSlotFilled = requestedReviewers.length > 0 ? 1 : 0;
  const filled = answeredCount + reviewerSlotFilled;
  const total = questions.length + 1;
  const requiredLeft = questions.filter((qq) => qq.required && !isAnswered(qq)).length;

  // Deadline: per-stage takes precedence over round endDate.
  const deadlineIso = r?.stageDeadlines?.collectEssay || r?.endDate;
  const deadlineDate = deadlineIso ? new Date(deadlineIso) : null;
  const deadlineLabel = deadlineDate
    ? deadlineDate.toLocaleDateString(i18n.language?.startsWith('en') ? 'en-GB' : 'ru-RU', {
        day: 'numeric',
        month: 'long',
      })
    : null;
  const daysLeft = deadlineDate
    ? Math.ceil((deadlineDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const isOverdue = !!deadlineDate && deadlineDate.getTime() < Date.now();

  // Reviewer pool. Already-picked users are removed from the Select options
  // so the picker only ever offers candidates not yet added.
  const users = (usersData as unknown as User[]) || [];
  const reviewerPool = users.filter((u) => u._id !== me?._id);
  const reviewerById = new Map(reviewerPool.map((u) => [u._id, u] as const));
  const pickedReviewers = requestedReviewers
    .map((rid) => reviewerById.get(rid))
    .filter((u): u is User => !!u);
  const availableOptions = reviewerPool
    .filter((u) => !requestedReviewers.includes(u._id))
    .map((u) => ({ value: u._id, label: u.position ? `${u.name} · ${u.position}` : u.name }));

  const addReviewer = (uid: string) => {
    if (isReviewersLocked || !uid) return;
    setRequestedReviewers((cur) => (cur.includes(uid) ? cur : [...cur, uid]));
  };
  const removeReviewer = (uid: string) => {
    if (isReviewersLocked) return;
    setRequestedReviewers((cur) => cur.filter((x) => x !== uid));
  };

  const railItems: RailItem[] = [
    ...questions.map((qq, i) => ({
      id: qq.id || `q-${i}`,
      title:
        (qq.text || '').length > 36
          ? (qq.text || '').slice(0, 36) + '…'
          : qq.text || `Вопрос ${i + 1}`,
      kind: 'check' as const,
      label: i + 1,
      required: !!qq.required,
      filled: isAnswered(qq),
    })),
    {
      id: 'reviewers',
      title: t('perfReview.desiredReviewers'),
      kind: 'icon',
      required: false,
      filled: requestedReviewers.length > 0,
    },
  ];

  const jumpTo = (sid: string) => {
    sectionNodes.current[sid]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const persistAnswers = async () => {
    const saved = await saveDraft.mutateAsync({
      id: id!,
      data: {
        answers: Object.entries(answers).map(([questionId, value]) => ({
          questionId,
          value: value as string | number | string[] | null,
        })),
        wantsGradePromotion,
      } as never,
    });
    syncFormCache(saved);
    // Ревьюеров бэкенд принимает только на сборе эссе; просроченное эссе отправляется без них.
    if (me?._id && f.round && !isReviewersLocked) {
      await setRequested.mutateAsync({
        roundId: f.round,
        userId: me._id,
        data: { reviewerIds: requestedReviewers },
      });
    }
  };

  const handleSubmit = async () => {
    try {
      await persistAnswers();
      await submit.mutateAsync({ id: id! });
      trackEvent(AnalyticsEvents.REVIEW_SUBMITTED, { type: 'self' });
      showToast({
        title: t('perfReview.submittedTitle'),
        message: t('perfReview.thanks'),
        color: 'teal',
      });
      navigate('/performance-review');
    } catch (e) {
      showToast({
        title: t('perfReview.submitFailed'),
        message: getErrorMessage(e, ''),
        color: 'red',
      });
    }
  };

  const autosaveBadge = (() => {
    if (readOnly)
      return (
        <Pill tone="accent" size="sm">
          <IconCheck size={11} />
          {t('perfReview.essaySubmitted')}
        </Pill>
      );
    if (autosaveStatus === 'saving') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            color: 'var(--se-muted)',
            fontWeight: 500,
          }}
        >
          <IconCloudUpload size={11} />
          {t('perfReview.autosaveSaving')}
        </span>
      );
    }
    if (autosaveStatus === 'error') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            color: 'var(--se-danger)',
            fontWeight: 500,
          }}
        >
          <IconAlertCircle size={11} />
          {t('perfReview.autosaveError')}
        </span>
      );
    }
    if (autosaveStatus === 'saved') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            color: 'var(--se-accent-deep)',
            fontWeight: 500,
          }}
        >
          <IconCloud size={11} />
          {t('perfReview.autosaveSaved')}
        </span>
      );
    }
    return null;
  })();

  const registerNode = (sid: string) => (el: HTMLDivElement | null) => {
    sectionNodes.current[sid] = el;
    if (el) el.dataset.sectionId = sid;
  };

  return (
    <div className="self-essay-v2">
      {/* Sticky breadcrumb header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 40px',
          borderBottom: '1px solid var(--se-line)',
          background: 'var(--se-bg)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
            color: 'var(--se-muted)',
          }}
        >
          <button
            onClick={() => navigate('/performance-review')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: 'none',
              color: 'var(--se-muted)',
              fontSize: 13,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <IconArrowLeft size={13} />
            {t('perfReview.backToHome')}
          </button>
          <span style={{ color: 'var(--se-soft)' }}>/</span>
          <span style={{ color: 'var(--se-ink)', fontWeight: 500 }}>
            {t('perfReview.selfReview')}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>{autosaveBadge}</div>
      </div>

      <div
        style={{
          padding: isNarrow ? '20px 16px 64px' : '28px 40px 80px',
          maxWidth: 1100,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isNarrow ? '1fr' : '260px minmax(0, 1fr)',
          gap: isNarrow ? 18 : 32,
          alignItems: 'flex-start',
        }}
      >
        {/* Left rail. На телефоне — под анкетой: сначала вопросы. */}
        <aside
          style={{
            order: isNarrow ? 1 : 0,
            position: isNarrow ? 'static' : 'sticky',
            top: 80,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <Card padding={20}>
            <div
              style={{
                fontSize: 11,
                color: 'var(--se-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                fontWeight: 600,
                marginBottom: 14,
              }}
            >
              {t('perfReview.essayStructure')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {railItems.map((it, i) => (
                <RailItemRow
                  key={it.id}
                  item={it}
                  active={it.id === activeId}
                  isLast={i === railItems.length - 1}
                  onClick={() => jumpTo(it.id)}
                />
              ))}
            </div>
          </Card>

          {canReopen && (
            <Card padding={18} style={{ border: '1px solid var(--se-line)' }}>
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--se-ink-2)',
                  lineHeight: 1.55,
                  marginBottom: 12,
                }}
              >
                {t('perfReview.selfReopenHint')}
              </div>
              <Button fullWidth variant="default" loading={isReopening} onClick={handleReopen}>
                {t('perfReview.selfReopen')}
              </Button>
            </Card>
          )}

          {/* На телефоне отправка — в конце анкеты, второй кнопки рядом не нужно. */}
          {!readOnly && !isNarrow && (
            <Card
              padding={18}
              style={{
                background: requiredLeft === 0 ? 'var(--se-accent-soft)' : 'var(--se-surface)',
                border: requiredLeft === 0 ? '1px solid transparent' : '1px solid var(--se-line)',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--se-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                {t('perfReview.submitSection')}
              </div>
              {requiredLeft > 0 ? (
                <>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--se-ink-2)',
                      lineHeight: 1.55,
                      marginBottom: 12,
                    }}
                  >
                    {t('perfReview.submitHintWithLeft', {
                      count: requiredLeft,
                    })}
                  </div>
                  <Button fullWidth disabled>
                    {t('perfReview.submit')}
                  </Button>
                </>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--se-ink-2)',
                      lineHeight: 1.55,
                      marginBottom: 12,
                    }}
                  >
                    {t('perfReview.submitHintReady')}
                  </div>
                  <Button
                    fullWidth
                    rightSection={<IconArrowRight size={14} />}
                    onClick={() => setConfirmOpen(true)}
                    loading={submit.isPending}
                  >
                    {t('perfReview.submit')}
                  </Button>
                </>
              )}
            </Card>
          )}
        </aside>

        {/* Main column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
          {/* Hero */}
          <Card padding={0} style={{ overflow: 'hidden' }}>
            <div style={{ padding: '28px 32px 22px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 14,
                  flexWrap: 'wrap',
                }}
              >
                {r?.title && (
                  <Pill tone="accent" size="sm">
                    <span
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: 999,
                        background: 'var(--se-accent)',
                        display: 'inline-block',
                      }}
                    />
                    {r.title}
                  </Pill>
                )}
                {!readOnly && deadlineLabel && (
                  <Pill tone={isOverdue ? 'danger' : 'warn'} size="sm">
                    <IconClock size={11} />
                    {isOverdue ? (
                      t('perfReview.overdueSince', { date: deadlineLabel })
                    ) : (
                      <>
                        <span className="num">{daysLeft}</span>&nbsp;
                        {t('perfReview.daysLeft', { date: deadlineLabel })}
                      </>
                    )}
                  </Pill>
                )}
              </div>
              <h1
                style={{
                  fontSize: 30,
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.15,
                  marginBottom: 10,
                  color: 'var(--se-ink)',
                  margin: 0,
                }}
              >
                {t('perfReview.selfReview')}
              </h1>
              <p
                style={{
                  fontSize: 14,
                  color: 'var(--se-muted)',
                  lineHeight: 1.6,
                  maxWidth: 640,
                  marginTop: 10,
                }}
              >
                {t('perfReview.selfIntro')}
              </p>
            </div>
            <div
              style={{
                padding: '16px 32px',
                background: 'var(--se-surface-2)',
                borderTop: '1px solid var(--se-line)',
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: 24,
                alignItems: 'center',
              }}
            >
              <Progress value={total > 0 ? (filled / total) * 100 : 0} color="teal" size="sm" />
              <div className="num" style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {filled}
                <span style={{ color: 'var(--se-muted)', fontWeight: 500 }}>/{total}</span>{' '}
                <span style={{ color: 'var(--se-muted)', fontWeight: 500 }}>
                  {t('perfReview.answersCount')}
                </span>
              </div>
              {requiredLeft > 0 ? (
                <Pill tone="warn" size="sm">
                  {t('perfReview.requiredLeft', {
                    count: requiredLeft,
                  })}
                </Pill>
              ) : (
                <Pill tone="accent" size="sm">
                  <IconCheck size={11} />
                  {t('perfReview.readyToSubmit')}
                </Pill>
              )}
            </div>
          </Card>

          {/* What was agreed at the last 1-on-1, right above the essay: the point of
              writing agreements down is that the next review checks them. */}
          {subject?._id && (
            <SubjectGoalsPanel
              userId={subject._id}
              hideWhenEmpty
              heading={t('perfReview.goals.fromLastReview')}
            />
          )}

          <Card
            padding={20}
            style={{
              borderColor: wantsGradePromotion ? 'rgba(18, 184, 134, 0.35)' : 'var(--se-line)',
              background: wantsGradePromotion
                ? 'linear-gradient(180deg, rgba(232, 250, 244, 0.95), var(--se-surface))'
                : 'var(--se-surface)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: isNarrow ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                gap: 18,
                flexDirection: isNarrow ? 'column' : 'row',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, minWidth: 0 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: wantsGradePromotion ? 'var(--se-accent)' : 'var(--se-accent-soft)',
                    color: wantsGradePromotion ? '#fff' : 'var(--se-accent-deep)',
                  }}
                >
                  <IconSparkles size={18} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 650,
                      color: 'var(--se-ink)',
                      letterSpacing: '-0.005em',
                      marginBottom: 4,
                    }}
                  >
                    {t('perfReview.wantsGradePromotionLabel')}
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--se-muted)' }}>
                    {t('perfReview.wantsGradePromotionDescription')}
                  </div>
                  {subject?.grade?.title && (
                    <div
                      style={{
                        marginTop: 8,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'var(--se-muted)',
                      }}
                    >
                      {t('perfReview.currentGrade')}:
                      {currentGradeLink ? (
                        <Anchor
                          href={currentGradeLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="never"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            color: 'var(--se-accent-deep)',
                            fontWeight: 600,
                          }}
                        >
                          {subject.grade.title}
                          <IconExternalLink size={11} />
                        </Anchor>
                      ) : (
                        <span style={{ color: 'var(--se-ink-2)', fontWeight: 600 }}>
                          {subject.grade.title}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <Switch
                color="teal"
                size="md"
                checked={wantsGradePromotion}
                disabled={readOnly}
                onChange={(event) => setWantsGradePromotion(event.currentTarget.checked)}
                styles={{ root: { flexShrink: 0 } }}
              />
            </div>
          </Card>

          {/* Questions */}
          {questions.map((qq, i) => {
            const sid = qq.id || `q-${i}`;
            const filledHere = isAnswered(qq);
            const val = stringValue(qq.id || '');
            const length = val.length;
            const isGood = length >= MIN_GOOD_CHARS;
            const isText = qq.type === 'text';

            return (
              <div key={sid} ref={registerNode(sid)} style={{ scrollMarginTop: 88 }}>
                <Card padding={24}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 14,
                      marginBottom: 14,
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        flexShrink: 0,
                        marginTop: 2,
                        background: filledHere ? 'var(--se-accent)' : 'var(--se-surface-2)',
                        color: filledHere ? '#fff' : 'var(--se-soft)',
                        border: filledHere ? 'none' : '1px solid var(--se-line-strong)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {filledHere ? <IconCheck size={14} /> : i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3
                        style={{
                          fontSize: 16,
                          fontWeight: 600,
                          lineHeight: 1.45,
                          letterSpacing: '-0.005em',
                          color: 'var(--se-ink)',
                          margin: 0,
                        }}
                      >
                        {qq.text}
                        {qq.required && (
                          <span style={{ color: 'var(--se-danger)', marginLeft: 4 }}>*</span>
                        )}
                      </h3>
                    </div>
                  </div>

                  {isText ? (
                    readOnly ? (
                      <div
                        style={{
                          padding: 14,
                          background: 'var(--se-surface-2)',
                          borderRadius: 'var(--se-radius)',
                          fontSize: 14,
                          color: 'var(--se-ink-2)',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {val || (
                          <span style={{ color: 'var(--se-soft)' }}>
                            {t('perfReview.noAnswer')}
                          </span>
                        )}
                      </div>
                    ) : (
                      <AutoSizeTextarea
                        value={val}
                        onChange={(v) => setAnswers((s) => ({ ...s, [qq.id ?? '']: v }))}
                      />
                    )
                  ) : (
                    <QuestionField
                      question={qq as Parameters<typeof QuestionField>[0]['question']}
                      value={answers[qq.id ?? ''] as string | number | string[] | null | undefined}
                      onChange={(v) => setAnswers((s) => ({ ...s, [qq.id ?? '']: v }))}
                      readOnly={readOnly}
                    />
                  )}

                  {isText && !readOnly && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 8,
                      }}
                    >
                      <span className="num" style={{ fontSize: 11, color: 'var(--se-muted)' }}>
                        {t('perfReview.chars', { count: length })}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: isGood ? 'var(--se-accent-deep)' : 'var(--se-muted)',
                          fontWeight: isGood ? 500 : 400,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        {isGood ? (
                          <>
                            <IconCheck size={11} />
                            {t('perfReview.answerEnough')}
                          </>
                        ) : length > 0 ? (
                          t('perfReview.answerCouldBeMore')
                        ) : null}
                      </span>
                    </div>
                  )}
                </Card>
              </div>
            );
          })}

          {/* Reviewers */}
          <div ref={registerNode('reviewers')} style={{ scrollMarginTop: 88 }}>
            <Card padding={24}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    flexShrink: 0,
                    marginTop: 2,
                    background:
                      requestedReviewers.length > 0 ? 'var(--se-accent)' : 'var(--se-surface-2)',
                    color: requestedReviewers.length > 0 ? '#fff' : 'var(--se-soft)',
                    border:
                      requestedReviewers.length > 0 ? 'none' : '1px solid var(--se-line-strong)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {requestedReviewers.length > 0 ? (
                    <IconCheck size={14} />
                  ) : (
                    <IconUsers size={14} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <h3
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      lineHeight: 1.45,
                      color: 'var(--se-ink)',
                      margin: 0,
                    }}
                  >
                    {t('perfReview.desiredReviewers')}
                  </h3>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--se-muted)',
                      lineHeight: 1.55,
                      marginTop: 6,
                    }}
                  >
                    {t('perfReview.desiredReviewersHint')}
                  </p>
                </div>
              </div>

              {!readOnly && isReviewersLocked && (
                <Text size="sm" c="dimmed">
                  {t('perfReview.reviewersLocked')}
                </Text>
              )}

              {!isReviewersLocked && availableOptions.length > 0 && (
                <Select
                  placeholder={t('perfReview.pickColleagues')}
                  data={availableOptions}
                  value={null}
                  onChange={(v) => v && addReviewer(v)}
                  searchable
                  clearable={false}
                  nothingFoundMessage={t('perfReview.nothingFound')}
                  mb={pickedReviewers.length > 0 ? 12 : 0}
                />
              )}

              {pickedReviewers.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 8,
                  }}
                >
                  {pickedReviewers.map((u) => (
                    <div
                      key={u._id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        background: 'var(--se-accent-soft)',
                        color: 'var(--se-accent-deep)',
                        border: '1px solid var(--se-accent)',
                        borderRadius: 'var(--se-radius)',
                      }}
                    >
                      <UserBadge user={u} size={28} style={{ flex: 1, minWidth: 0 }} />
                      {!isReviewersLocked && (
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={() => removeReviewer(u._id)}
                          aria-label={t('perfReview.removeReviewer')}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {pickedReviewers.length === 0 && isReviewersLocked && (
                <Text size="sm" c="dimmed">
                  {t('perfReview.noReviewersPicked')}
                </Text>
              )}
            </Card>
          </div>

          {/* Inline submit reminder */}
          {!readOnly && (
            <Card
              padding={20}
              style={{
                background: 'var(--se-surface-2)',
                borderStyle: 'dashed',
                border: '1px dashed var(--se-line-strong)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      marginBottom: 2,
                      color: 'var(--se-ink)',
                    }}
                  >
                    {requiredLeft > 0 ? t('perfReview.readyToSubmitQ') : t('perfReview.allFilled')}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--se-muted)' }}>
                    {requiredLeft > 0
                      ? t('perfReview.submitHintWithLeft', {
                          count: requiredLeft,
                        })
                      : t('perfReview.submitHintReady')}
                  </div>
                </div>
                <Button
                  rightSection={<IconArrowRight size={14} />}
                  disabled={requiredLeft > 0}
                  loading={submit.isPending}
                  onClick={() => setConfirmOpen(true)}
                >
                  {t('perfReview.submit')}
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={null}
        size="md"
        centered
        withCloseButton={false}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'var(--se-accent-soft)',
              color: 'var(--se-accent-deep)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconSparkles size={18} />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: 'var(--se-ink)' }}>
            {t('perfReview.submitConfirmTitle')}
          </h3>
        </div>
        <p
          style={{
            fontSize: 13,
            color: 'var(--se-muted)',
            lineHeight: 1.55,
            marginBottom: 18,
          }}
        >
          {t('perfReview.submitConfirmBody')}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="default" onClick={() => setConfirmOpen(false)}>
            {t('perfReview.submitConfirmCancel')}
          </Button>
          <Button
            rightSection={<IconArrowRight size={14} />}
            loading={submit.isPending}
            onClick={async () => {
              await handleSubmit();
              setConfirmOpen(false);
            }}
          >
            {t('perfReview.submitConfirmAccept')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};
